import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, MessageCircle, Phone } from 'lucide-react';
import { mockData } from '../data/mock';
import { revealVariants, staggerContainer } from '../utils/motion';
import { useInViewOnce } from '../hooks/useInViewOnce';
import { MotionButton } from '../components/ui/motion-button';

export const ServiceDetail = () => {
  const { serviceId } = useParams();
  const service = mockData.services.find(s => s.id === serviceId);
  const [ref, isInView] = useInViewOnce();

  if (!service) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <Link to="/services" className="text-[#1F3C88] hover:text-[#FF5C8A] font-semibold">
            ← Back to Services
          </Link>
        </div>
      </div>
    );
  }

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(`Hello SAX! I'm interested in your ${service.name} services. Could we discuss my requirements?`);
    const whatsappUrl = `https://wa.me/${mockData.company.whatsapp.replace(/\s+/g, '').replace(/^\+/, '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCallClick = () => {
    window.open(`tel:${mockData.company.phone}`, '_self');
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-transparent to-slate-50" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="max-w-4xl mx-auto"
          >
            <motion.div variants={revealVariants} className="mb-6">
              <Link
                to="/services"
                className="inline-flex items-center space-x-2 text-[#1F3C88] hover:text-[#FF5C8A] transition-colors font-medium"
              >
                <ArrowLeft size={20} />
                <span>Back to Services</span>
              </Link>
            </motion.div>

            <motion.div
              variants={revealVariants}
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A]/10 to-[#FF8C42]/10 text-[#1F3C88] px-4 py-2 rounded-full text-sm font-medium mb-8"
            >
              <span>Service Details</span>
            </motion.div>

            <motion.h1 
              variants={revealVariants}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {service.name}
            </motion.h1>

            <motion.p 
              variants={revealVariants}
              className="text-xl text-gray-600 leading-relaxed mb-8"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {service.description}
            </motion.p>

            <motion.div 
              variants={revealVariants}
              className="flex flex-col sm:flex-row gap-4"
            >
              <MotionButton
                variant="secondary"
                size="lg"
                onClick={handleWhatsAppClick}
              >
                <div className="flex items-center space-x-2">
                  <MessageCircle size={20} />
                  <span>Discuss on WhatsApp</span>
                </div>
              </MotionButton>

              <MotionButton
                variant="outline"
                size="lg"
                onClick={handleCallClick}
              >
                <div className="flex items-center space-x-2">
                  <Phone size={20} />
                  <span>Call Now</span>
                </div>
              </MotionButton>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                ref={ref}
                variants={staggerContainer}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                className="space-y-12"
              >
                {/* Benefits */}
                <motion.div variants={revealVariants}>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Benefits</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {service.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-center space-x-3 bg-gradient-to-r from-slate-50 to-blue-50 p-4 rounded-xl">
                        <Check size={20} className="text-[#FF5C8A] flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Process */}
                <motion.div variants={revealVariants}>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Process</h2>
                  <div className="space-y-4">
                    {service.process.map((step, index) => (
                      <div key={index} className="flex items-center space-x-4 p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow">
                        <div className="w-8 h-8 bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                          {index + 1}
                        </div>
                        <span className="text-gray-800 font-medium">{step}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* FAQ Section */}
                <motion.div variants={revealVariants}>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
                  <div className="space-y-4">
                    {/* Mock FAQ data */}
                    {[
                      {
                        question: `What makes SAX's ${service.name} different?`,
                        answer: "Our multi-sector expertise and proven methodology ensure we deliver tailored solutions that drive measurable results for your specific industry needs."
                      },
                      {
                        question: "How long does a typical project take?",
                        answer: "Project timelines vary based on scope and complexity. During our initial consultation, we'll provide a detailed timeline that aligns with your business objectives."
                      },
                      {
                        question: "What level of involvement is required from our team?",
                        answer: "We work collaboratively with your team to ensure knowledge transfer and sustainable implementation. The level of involvement is tailored to your preferences and internal capabilities."
                      }
                    ].map((faq, index) => (
                      <div key={index} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                        <h3 className="font-semibold text-gray-900 mb-2">{faq.question}</h3>
                        <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                className="sticky top-8 space-y-8"
              >
                {/* KPIs */}
                <motion.div variants={revealVariants} className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Key Performance Indicators</h3>
                  <div className="space-y-4">
                    {service.kpis.map((kpi, index) => (
                      <div key={index} className="text-center bg-white rounded-xl p-4 shadow-sm">
                        <div className="text-3xl font-bold text-[#1F3C88] mb-1">{kpi.value}</div>
                        <div className="text-sm text-gray-600">{kpi.label}</div>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Contact CTA */}
                <motion.div variants={revealVariants} className="bg-gradient-to-br from-[#1F3C88] to-[#2d4ba3] rounded-2xl p-6 text-white">
                  <h3 className="text-xl font-bold mb-4">Ready to Get Started?</h3>
                  <p className="text-blue-100 mb-6 leading-relaxed">
                    Let's discuss how our {service.name.toLowerCase()} expertise can help achieve your project goals.
                  </p>
                  
                  <div className="space-y-3">
                    <button
                      onClick={handleWhatsAppClick}
                      className="w-full bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-shadow"
                    >
                      WhatsApp Consultation
                    </button>
                    
                    <Link
                      to="/request-service"
                      className="block w-full bg-white/20 backdrop-blur-sm text-white py-3 rounded-xl font-semibold text-center hover:bg-white/30 transition-colors"
                    >
                      Request Proposal
                    </Link>
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/20 text-center text-blue-100 text-sm">
                    <div className="flex items-center justify-center space-x-2">
                      <Phone size={16} />
                      <span>{mockData.company.phone}</span>
                    </div>
                  </div>
                </motion.div>

                {/* Related Services */}
                <motion.div variants={revealVariants} className="bg-white border border-gray-200 rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Other Services</h3>
                  <div className="space-y-3">
                    {mockData.services.filter(s => s.id !== serviceId).slice(0, 3).map((relatedService) => (
                      <Link
                        key={relatedService.id}
                        to={`/services/${relatedService.id}`}
                        className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{relatedService.name}</div>
                        <div className="text-sm text-gray-600 mt-1">{relatedService.description}</div>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};